import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { openAlert, openConfirm } from '../../utils/common';
import { useValidation, useSecurityMonitor } from '../../hooks/useValidation';
import { sanitizeApiRequest, sanitizeString } from '../../utils/xssProtection';
import '../../assets/css/common.css';
import '../../assets/css/main.css';
import '../../assets/css/adm_custom.css';
import SectionContainer from '../../components/UI/SectionContainer';
import Spinner from '../../components/UI/Spinner';
import faqService from '../../services/faqService';

const FaqFormPage = () => {
  const { action, id } = useParams();
  const isEdit = action === 'edit';
  const navigate = useNavigate();

  const [quesKo, setQuesKo] = useState('');
  const [ansKo, setAnsKo] = useState('');
  const [quesEn, setQuesEn] = useState('');
  const [ansEn, setAnsEn] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedLang, setSelectedLang] = useState('ko');
  const [errors, setErrors] = useState({});
  const [showSecurityWarning, setShowSecurityWarning] = useState(false);

  // 보안 모니터링 훅
  const {
    securityLog,
    riskLevel,
    logSecurityEvent,
    clearSecurityLog,
    eventCount
  } = useSecurityMonitor();

  const fetchDetail = useCallback(async () => {
    setLoading(true);
    try {
      const result = await faqService.getFAQDetail(id);
      if (result.success) {
        const val = result.data;
        // 데이터 로드 시에도 XSS 방지를 위한 정화 적용
        setQuesKo(sanitizeString(val.ques_ko || '', 'html'));
        setAnsKo(sanitizeString(val.answer_ko || '', 'html'));
        setQuesEn(sanitizeString(val.ques_en || '', 'html'));
        setAnsEn(sanitizeString(val.answer_en || '', 'html'));
      } else {
        openAlert({ description: result.message });
      }
    } catch (e) {
      console.error(e);
      openAlert({ description: '데이터 로드 중 오류가 발생했습니다.' });
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (isEdit) fetchDetail();
  }, [isEdit, fetchDetail]);

  const handleSaveConfirm = useCallback(async () => {
    setLoading(true);
    try {
      // 1. 클라이언트 검증
      const validation = validateFaqData();
      
      if (!validation.isValid) {
        const firstError = Object.values(validation.errors)[0];
        openAlert({ title: '입력 오류', description: firstError });
        setLoading(false);
        return;
      }

      // 2. 보안 위험 감지 시 추가 확인
      if (eventCount > 0 && riskLevel === 'HIGH') {
        const confirmResult = window.confirm(
          '입력 데이터에서 보안 위험이 감지되었습니다. 계속 진행하시겠습니까?'
        );
        if (!confirmResult) {
          setLoading(false);
          return;
        }
      }

      // 3. API 요청 데이터 정화
      const safeFaqData = sanitizeApiRequest({
        ques_ko: quesKo,
        answer_ko: ansKo,
        ques_en: quesEn,
        answer_en: ansEn,
        addSummerNoteImages: [],
        delSummerNoteImages: []
      }, '/faq');
      
      // 4. 보안 이벤트 로깅
      if (eventCount > 0) {
        console.warn('FAQ save attempt with security events:', {
          eventCount,
          riskLevel,
          action: isEdit ? 'update' : 'create'
        });
      }
      
      const result = isEdit 
        ? await faqService.updateFAQ(id, safeFaqData)
        : await faqService.createFAQ(safeFaqData);
      
      if (result.success) {
        // 성공 시 보안 로그 정리
        clearSecurityLog();
        openAlert({ 
          description: result.message,
          response_method: () => navigate('/settings/faq')
        });
      } else {
        openAlert({ description: result.message });
      }
    } catch (e) {
      console.error('FAQ save error:', e);
      
      // 오류 시 보안 이벤트 로깅
      logSecurityEvent({
        type: 'FAQ_SAVE_ERROR',
        field: 'form',
        riskLevel: 'MEDIUM',
        message: 'FAQ save process error',
        error: e.message
      });
      
      openAlert({ description: '저장 중 오류가 발생했습니다.' });
    } finally {
      setLoading(false);
    }
  }, [quesKo, ansKo, quesEn, ansEn, isEdit, id, navigate, eventCount, riskLevel, clearSecurityLog, logSecurityEvent]);

  // Expose for confirm callback
  useEffect(() => {
    window.handleSaveConfirm = handleSaveConfirm;
    return () => { delete window.handleSaveConfirm; };
  }, [handleSaveConfirm]);

  /**
   * FAQ 데이터 검증
   */
  const validateFaqData = useCallback(() => {
    const newErrors = {};
    
    // 최소 한 개의 언어 버전이 있는지 확인
    const hasKoreanVersion = quesKo.trim() && ansKo.trim();
    const hasEnglishVersion = quesEn.trim() && ansEn.trim();
    
    if (!hasKoreanVersion && !hasEnglishVersion) {
      newErrors.general = '최소 한 개의 언어 질문과 답변을 모두 입력해주세요.';
    }
    
    // 각 필드 길이 검증
    if (quesKo.trim() && quesKo.length > 500) {
      newErrors.quesKo = '한국어 질문은 500자 이내로 입력해주세요.';
    }
    if (quesEn.trim() && quesEn.length > 500) {
      newErrors.quesEn = '영어 질문은 500자 이내로 입력해주세요.';
    }
    if (ansKo.trim() && ansKo.length > 5000) {
      newErrors.ansKo = '한국어 답변은 5000자 이내로 입력해주세요.';
    }
    if (ansEn.trim() && ansEn.length > 5000) {
      newErrors.ansEn = '영어 답변은 5000자 이내로 입력해주세요.';
    }
    
    setErrors(newErrors);
    
    return {
      isValid: Object.keys(newErrors).length === 0,
      errors: newErrors
    };
  }, [quesKo, ansKo, quesEn, ansEn]);

  /**
   * 안전한 입력 처리 (XSS 방지)
   */
  const handleSafeInput = useCallback((field, value) => {
    // HTML 콘텐츠 정화
    const sanitized = sanitizeString(value, 'html');
    
    // XSS 시도 감지
    if (value !== sanitized) {
      logSecurityEvent({
        type: 'XSS_ATTEMPT',
        field,
        riskLevel: 'HIGH',
        message: 'Potential XSS content detected in FAQ field',
        originalLength: value.length,
        sanitizedLength: sanitized.length
      });
      
      setShowSecurityWarning(true);
    }
    
    // 필드별 상태 업데이트
    switch (field) {
      case 'quesKo':
        setQuesKo(sanitized);
        break;
      case 'ansKo':
        setAnsKo(sanitized);
        break;
      case 'quesEn':
        setQuesEn(sanitized);
        break;
      case 'ansEn':
        setAnsEn(sanitized);
        break;
    }
    
    // 실시간 검증
    setTimeout(() => validateFaqData(), 300);
  }, [logSecurityEvent, validateFaqData]);

  const handleSubmit = () => {
    const validation = validateFaqData();
    
    if (!validation.isValid) {
      const firstError = Object.values(validation.errors)[0];
      openAlert({ description: firstError });
      return;
    }
    
    openConfirm({
      title: '알림',
      description: '작성하신 게시글을 저장하시겠습니까?',
      positive_method: 'handleSaveConfirm'
    });
  };

  /**
   * 보안 경고 닫기
   */
  const handleCloseSecurityWarning = () => {
    setShowSecurityWarning(false);
  };

  /**
   * 입력 필드 스타일 (오류 및 보안 위험 시 강조)
   */
  const getInputClassName = (fieldName) => {
    let className = 'input-xxl';
    
    if (errors[fieldName]) {
      className += ' error';
    }
    
    return className;
  };

  return (
    <SectionContainer
      title={isEdit ? "FAQ 수정" : "FAQ 작성"}
      className="faq_edit edit write"
    >
      <Spinner visible={loading} />

      {/* 보안 경고 표시 */}
      {showSecurityWarning && (
        <div className="alert alert-warning mb-3">
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <strong>⚠️ 보안 경고</strong>
              <p className="mb-0">
                입력 데이터에서 의심스러운 패턴이 감지되어 정화되었습니다.
              </p>
              <small>
                보안 이벤트: {eventCount}건, 위험도: {riskLevel}
              </small>
            </div>
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              onClick={handleCloseSecurityWarning}
            >
              확인
            </button>
          </div>
        </div>
      )}

      {/* 전체 오류 메시지 */}
      {errors.general && (
        <div className="alert alert-danger mb-3">{errors.general}</div>
      )}

      <div className="content_area">
        <div className="lang_tab_area">
          {["ko", "en"].map((lang) => (
            <div
              key={lang}
              className={`lang_tab ${selectedLang === lang ? "active" : ""}`}
              data-lang={lang}
              onClick={() => setSelectedLang(lang)}
            >
              {lang === "ko" ? "한국어" : "English"}
            </div>
          ))}
        </div>
        <div className="modal-content-wrap">
          {["ko", "en"].map((lang) => (
            <div
              key={lang}
              className="lang_content"
              style={{ display: selectedLang === lang ? "block" : "none" }}
            >
              <div className="notice_area">
                <h3 className="mb-1 tit-h3">
                  {lang === "ko" ? "질문사항 (한국어)" : "Question (English)"}
                </h3>
                <input
                  type="text"
                  className={getInputClassName(
                    lang === "ko" ? "quesKo" : "quesEn"
                  )}
                  value={lang === "ko" ? quesKo : quesEn}
                  onChange={(e) =>
                    handleSafeInput(
                      lang === "ko" ? "quesKo" : "quesEn",
                      e.target.value
                    )
                  }
                  placeholder={
                    lang === "ko"
                      ? "질문을 입력해주세요."
                      : "Enter your question"
                  }
                  maxLength={500}
                />
                {errors[lang === "ko" ? "quesKo" : "quesEn"] && (
                  <div className="text-danger mt-1 small">
                    {errors[lang === "ko" ? "quesKo" : "quesEn"]}
                  </div>
                )}
                <div className="text-muted small mt-1">
                  {(lang === "ko" ? quesKo : quesEn).length}/500자
                </div>
              </div>
              <div className="notice_area">
                <h3 className="mb-1 tit-h3">
                  {lang === "ko" ? "답변내용 (한국어)" : "Answer (English)"}
                </h3>
                <textarea
                  style={{ resize: "none", height: 300 }}
                  className={getInputClassName(
                    lang === "ko" ? "ansKo" : "ansEn"
                  )}
                  value={lang === "ko" ? ansKo : ansEn}
                  onChange={(e) =>
                    handleSafeInput(
                      lang === "ko" ? "ansKo" : "ansEn",
                      e.target.value
                    )
                  }
                  placeholder={
                    lang === "ko"
                      ? "답변 내용을 입력해주세요."
                      : "Enter your answer"
                  }
                  maxLength={5000}
                />
                {errors[lang === "ko" ? "ansKo" : "ansEn"] && (
                  <div className="text-danger mt-1 small">
                    {errors[lang === "ko" ? "ansKo" : "ansEn"]}
                  </div>
                )}
                <div className="text-muted small mt-1">
                  {(lang === "ko" ? ansKo : ansEn).length}/5000자
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className="button_wrap">
          <div className="but-flex">
            <button
              className="btn btn-md tertiary mr-1"
              onClick={() => navigate("/settings/faq")}
              disabled={loading}
            >
              취소하기
            </button>
            <button
              className="btn btn-md primary"
              onClick={handleSubmit}
              disabled={loading || Object.keys(errors).length > 0}
            >
              {loading ? "저장 중..." : "저장하기"}
            </button>
          </div>
        </div>

        {/* 개발 모드에서 보안 상태 표시 */}
        {process.env.NODE_ENV === "development" && eventCount > 0 && (
          <div className="mt-3 p-3 bg-light border rounded">
            <div className="small text-muted">
              <strong>보안 모니터링 상태:</strong>
              <div>
                위험도:{" "}
                <span
                  className={`badge badge-${
                    riskLevel === "HIGH"
                      ? "danger"
                      : riskLevel === "MEDIUM"
                      ? "warning"
                      : "success"
                  }`}
                >
                  {riskLevel}
                </span>
              </div>
              <div>보안 이벤트: {eventCount}건</div>
            </div>
          </div>
        )}
      </div>
    </SectionContainer>
  );
};

export default FaqFormPage;
